import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { PerformanceService } from 'src/app/nudge-shared/performance/performance/performance.service';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { AppStateService } from 'src/app/store/app-state.service';
import { NudgePageService } from 'src/app/nudge-v2/nudge-page.service';
import { NudgeLibraryPortService } from 'src/app/nudge-v2/nudge-library/nudge-library-port.service';
import {
  getWinningExperiments,
  WinningExperiment,
} from '../../winning-experiments';

/**
 * Nudge "Revenue Optimization" card for the Shopify LIVE dashboard.
 *
 * This reproduces the Nudge part of the normal dashboard (dashboard.component,
 * "Revenue Optimization" block): the Visits/Orders/Revenue-uplift table and the
 * CR & RPV comparison bars. It uses the same data source (PerformanceService
 * getBarChartCr / getBarChartRpv) and shares the same styles via
 * performance.component.css so it renders identically.
 *
 * The Volvo test-drive, nudge-lite and long-range (>3 months) branches from the
 * dashboard are intentionally omitted — they don't apply to a standard Shopify
 * store and aren't part of this view.
 *
 * Targets on the new nudge view (`ab_testing_tool`, see utils/nudge-version)
 * get the dashboard's "Top Winning Experiments" table instead of the bars.
 */
@Component({
  selector: 'app-shopify-nudge-card',
  templateUrl: './shopify-nudge-card.component.html',
  styleUrls: [
    '../../../nudge/performance/performance/performance.component.css',
    '../../dashboard.component.css',
    './shopify-nudge-card.component.css',
  ],
})
export class ShopifyNudgeCardComponent implements OnInit, OnDestroy {
  private performanceService = inject(PerformanceService);
  private targetService = inject(CoreTargetService);
  private localStorageService = inject(LocalStorageService);
  private appState = inject(AppStateService);
  private router = inject(Router);
  private nudgePage = inject(NudgePageService);
  private portService = inject(NudgeLibraryPortService);

  private subs = new Subscription();
  private winningExperimentsRequest?: Subscription;
  private campaignsRequest?: Subscription;

  // The flag arrives with the target list, so it can flip after init.
  // toObservable needs an injection context, so it is created here, not in ngOnInit.
  private readonly abTestingView$ = toObservable(this.appState.abTestingView);
  public useWinningExperiments = false;
  public winningExperiments: WinningExperiment[] = [];
  public totalWinningExperiments = 0;
  public winningExperimentsLoading = true;
  public winningExperimentsError = false;
  // Whether the target has any campaign yet; null while it is being checked.
  // With none, the card offers suggested nudges instead of a new campaign.
  public hasCampaigns: boolean | null = null;
  readonly winningExperimentMetrics = ['rpv', 'cr', 'aov'] as const;

  public nudgeDataFirstLoad = true;
  public nudgeDataLoading = true;
  public nudgeLite = false;
  public nudge_blurred_show = false;

  public chartDataCr: any[] = [
    { data: [], label: 'phrases.nudges_overall' },
    { data: [], label: 'words.control' },
  ];

  public barChartCr: any = [
    {
      name: 'phrases.nudges_overall',
      value: 0,
      height: 0,
      diff: 0,
      tooltip: '',
    },
    { name: 'words.control', value: 0, height: 0, diff: 0, control: true },
  ];
  public barChartCrAxis: any = [];

  public barChartRpv: any = [
    {
      name: 'phrases.nudges_overall',
      value: 0,
      height: 0,
      diff: 0,
      tooltip: '',
    },
    { name: 'words.control', value: 0, height: 0, diff: 0, control: true },
  ];
  public barChartRpvAxis: any = [];

  public incentiveBarChart: any = {
    crorderAmount: {
      incentivesAmount: { visits: '', orders: '' },
      controlAmount: { visits: '', orders: '' },
    },
    selectedPlatform: 'overall',
  };

  public revUplift: number;
  public revUplift_color: any;

  private pendingCalls = 0;

  ngOnInit(): void {
    this.subs.add(
      this.abTestingView$.pipe(distinctUntilChanged()).subscribe((enabled) => {
        this.useWinningExperiments = enabled;
        if (enabled) this.loadWinningExperiments();
        else this.fetchNudgeData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.winningExperimentsRequest?.unsubscribe();
    this.campaignsRequest?.unsubscribe();
    this.subs.unsubscribe();
  }

  // Same source and ranking as DashboardComponent.loadWinningExperiments.
  public loadWinningExperiments(): void {
    this.loadCampaigns();
    this.winningExperimentsRequest?.unsubscribe();
    this.winningExperimentsLoading = true;
    this.winningExperimentsError = false;

    this.winningExperimentsRequest = this.performanceService
      .getPerformanceOverview({
        startDate: this.localStorageService.get('startDate'),
        endDate: this.localStorageService.get('endDate'),
        target: this.targetService.getSelectedTarget('target'),
        device: '',
        active: false,
        siteID: 'Show All',
      })
      .subscribe({
        next: (response) => {
          this.winningExperimentsLoading = false;
          this.winningExperimentsError = Boolean(response?.error);
          if (this.winningExperimentsError) return;
          const summary = getWinningExperiments(
            response,
            this.localStorageService.get('role'),
          );
          this.winningExperiments = summary.rows;
          this.totalWinningExperiments = summary.total;
        },
        error: () => {
          this.winningExperimentsLoading = false;
          this.winningExperimentsError = true;
        },
      });
  }

  private loadCampaigns(): void {
    this.campaignsRequest?.unsubscribe();
    this.hasCampaigns = null;
    this.campaignsRequest = this.portService
      .listCampaigns(this.targetService.getSelectedTarget('target') || '')
      .subscribe({
        next: (campaigns) => (this.hasCampaigns = campaigns.length > 0),
        // Unknown: fall back to the usual "Create a campaign".
        error: () => (this.hasCampaigns = true),
      });
  }

  // With no campaigns, the Campaigns tab leads with the site's suggested nudges.
  public getSuggestedNudges(): void {
    this.router.navigate(['/master-preview'], {
      queryParams: { tab: 'campaigns' },
    });
  }

  public openNudges(): void {
    this.router.navigate(['/master-preview']);
  }

  // Same as "New campaign" on the Campaigns tab: the Nudge Library opens on
  // the New campaign form. The request waits in NudgePageService until the
  // library tab picks it up.
  public createCampaign(): void {
    this.nudgePage.startCampaignInLibrary();
    this.router.navigate(['/master-preview'], {
      queryParams: { tab: 'library' },
    });
  }

  private fetchNudgeData(): void {
    const data = {
      startDate: this.localStorageService.get('startDate'),
      endDate: this.localStorageService.get('endDate'),
      target: this.targetService.getSelectedTarget('target'),
      device: '',
      visitType: '',
    };

    this.nudgeDataLoading = true;
    this.pendingCalls = 2;

    this.getChartCr(data);
    this.getChartRpv(data);
  }

  private markCallDone(): void {
    this.pendingCalls--;
    if (this.pendingCalls <= 0) {
      this.nudgeDataLoading = false;
      this.nudgeDataFirstLoad = false;
    }
  }

  private getChartCr(dateRange: any): void {
    this.subs.add(
      this.performanceService.getBarChartCr(dateRange).subscribe({
        next: (info: any) => {
          if (info.incentives == null) info.incentives = 0;
          if (info.control == null) info.control = 0;

          this.barChartCr[0].value = info.incentives;
          this.barChartCr[1].value = info.control;

          this.barChartCr[0].tooltip = this.formatPvalue(info.p);
          this.barChartCr[0].tooltip_translate = this.formatPvalue(
            info.p,
            true,
          );
          this.barChartCr[0].diff = this.increaseDecreaseDiff(
            info.incentives,
            info.control,
            true,
          );

          this.barChartCrAxis = this.generateEvenPoints(
            this.barChartCr,
            5,
          ).reverse();
          this.barChartCr = this.setProportionalHeights(this.barChartCr, 90);

          this.incentiveBarChart.crorderAmount.incentivesAmount.visits =
            info.incentivesInfo?.visits != null
              ? this.formatNumber(info.incentivesInfo.visits)
              : '0';
          this.incentiveBarChart.crorderAmount.incentivesAmount.orders =
            info.incentivesInfo?.orders != null
              ? this.formatNumber(info.incentivesInfo.orders)
              : '0';
          this.incentiveBarChart.crorderAmount.controlAmount.visits =
            info.controlInfo?.visits != null
              ? this.formatNumber(info.controlInfo.visits)
              : '0';
          this.incentiveBarChart.crorderAmount.controlAmount.orders =
            info.controlInfo?.orders != null
              ? this.formatNumber(info.controlInfo.orders)
              : '0';

          this.markCallDone();
        },
        error: () => this.markCallDone(),
      }),
    );
  }

  private getChartRpv(dateRange: any): void {
    this.subs.add(
      this.performanceService.getBarChartRpv(dateRange).subscribe({
        next: (info: any) => {
          if (info?.incentives == null && info?.control == null) {
            info.incentives = 0;
            info.control = 0;
          } else {
            info.incentives =
              info.incentivesInfo['revenue'] /
              Number(info.incentivesInfo['visits']);
            info.control =
              info.controlInfo['revenue'] / Number(info.controlInfo['visits']);

            // info.incentives = Number(info.incentives.toFixed(2));
            // info.control = Number(info.control.toFixed(2));
          }

          const incRev = info.incentives;
          const cntRev = info.control;
          const infVisits = info.incentivesInfo
            ? info.incentivesInfo['visits']
            : 0;

          this.revUplift = this.calcUplift(
            isNaN(incRev) ? 0 : incRev,
            isNaN(cntRev) ? 0 : cntRev,
            isNaN(infVisits) ? 0 : infVisits,
          );
          if (info.sumRevenueUpliftCount) {
            this.revUplift = info.sumRevenueUpliftCount;
          }
          this.checkUpliftColor();

          this.barChartRpv[0].value = info.incentives;
          this.barChartRpv[1].value = info.control;
          this.barChartRpv[0].tooltip = this.formatPvalue(info.p);
          this.barChartRpv[0].tooltip_translate = this.formatPvalue(
            info.p,
            true,
          );
          this.barChartRpv[0].diff = this.increaseDecreaseDiff(
            info.incentives,
            info.control,
            true,
          );

          this.barChartRpvAxis = this.generateEvenPoints(
            this.barChartRpv,
            5,
          ).reverse();
          this.barChartRpv = this.setProportionalHeights(this.barChartRpv, 90);

          this.markCallDone();
        },
        error: () => this.markCallDone(),
      }),
    );
  }

  // ── helpers copied from dashboard.component so the maths/formatting match ──
  private formatNumber(num: any): string {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }

  private formatPvalue(num: any, translate: boolean = false): any {
    if (translate) {
      if (num == null) return '';
      num = num.toFixed(2);
      return num <= 0.1
        ? { score: num, label: 'words.significant' }
        : { score: num, label: 'phrases.not_significant' };
    }

    if (num == null) return '';
    num = num.toFixed(2);
    return num <= 0.1 ? num + ' (Significant)' : num + ' (Not Significant)';
  }

  private increaseDecreaseDiff(
    incentive: any,
    control: any,
    noHTML: boolean = false,
  ): string {
    let diff = incentive / control - 1;
    diff = diff * 100;

    if (isNaN(diff) || diff === Infinity || diff === -Infinity) {
      return '';
    }

    if (diff < 0) {
      if (noHTML) return '' + diff.toFixed(2).replace('-', '- ') + '%';
      return (
        "<span style='color: #458c30;'>" +
        diff.toFixed(2).replace('-', '- ') +
        '%</span>'
      );
    }
    if (noHTML) return '+ ' + diff.toFixed(2) + '%';
    return "<span style='color: #ff9292ed;'>+ " + diff.toFixed(2) + '%</span>';
  }

  private generateEvenPoints(data: any, numPoints: any): string[] {
    const validValues = data
      .map((item: any) => item.value)
      .filter(
        (value: any) => value !== null && value !== undefined && !isNaN(value),
      );

    if (validValues.length === 0) return [];

    const maxValue = Math.max(...validValues);
    const minValue = Math.min(...validValues);

    const points = [];

    if (maxValue <= 0) {
      const roundedMin = Math.floor(minValue / 2) * 2;
      const interval = Math.abs(roundedMin) / (numPoints - 1);
      for (let i = 0; i < numPoints; i++) {
        points.push((0 - interval * i).toFixed(0));
      }
    } else {
      const roundedMax = Math.ceil(maxValue / 2) * 2;
      const interval = roundedMax / (numPoints - 1);
      for (let i = 0; i < numPoints; i++) {
        points.push((interval * i).toFixed(0));
      }
    }

    return points;
  }

  private setProportionalHeights(data: any, maxHeightPercentage: any): any {
    const validValues = data
      .map((item: any) => item.value)
      .filter(
        (value: any) => value !== null && value !== undefined && !isNaN(value),
      );

    const allNegative = validValues.every((value: any) => value < 0);
    const maxValue = allNegative
      ? Math.min(...validValues)
      : Math.max(...validValues);

    data.forEach((item: any) => {
      if (item.value === 0 || item.value == 'NaN' || isNaN(item.value)) {
        item.height = 3;
        item.value = 0;
      } else {
        item.height = (item.value / maxValue) * maxHeightPercentage;
        item.height = Math.round(item.height * 100) / 100;
      }
    });

    return data;
  }

  private calcUplift(inc: any, con: any, visits: any): number {
    return (inc - con) * visits;
  }

  private checkUpliftColor(): void {
    if (this.revUplift < 0) this.revUplift_color = false;
    else if (this.revUplift > 0) this.revUplift_color = true;
    else this.revUplift_color = 0;
  }
}
